"use client"

import { useState } from "react"
import { Meeting, MeetingConsent, MeetingConsentAttestedStatus } from "@/schema"
import { Button, Label, RadioGroup, RadioGroupItem } from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { CircleCheck, CircleX, Loader2, SearchX, ShieldCheck } from "lucide-react"

import { useAttestMeetingConsent } from "@/hooks/api/meetings"
import { ConfirmDialog } from "@/components/dialogs"

interface MeetingConsentCardProps {
  meeting: Meeting
  onSeek?: (seconds: number) => void
  compact?: boolean
}

function formatTimestamp(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`
}

function Timestamp({ seconds, onSeek }: { seconds: number; onSeek?: (seconds: number) => void }) {
  const label = formatTimestamp(seconds)
  if (!onSeek) return <span>{label}</span>
  return (
    <button type="button" className="underline underline-offset-2" onClick={() => onSeek(seconds)}>
      {label}
    </button>
  )
}

function InferredLine({ consent, onSeek }: { consent: MeetingConsent; onSeek?: (seconds: number) => void }) {
  const seconds = consent.inferred_timestamp_seconds
  if (consent.inferred_status === "granted") {
    return (
      <span className="text-green-accent inline-flex items-center gap-1.5">
        <CircleCheck className="size-4" />
        Consent detected
        {seconds !== null && (
          <>
            {" "}
            at <Timestamp seconds={seconds} onSeek={onSeek} />
          </>
        )}
      </span>
    )
  }
  if (consent.inferred_status === "declined") {
    return (
      <span className="text-red-accent inline-flex items-center gap-1.5">
        <CircleX className="size-4" />
        Client declined to be recorded
        {seconds !== null && (
          <>
            {" "}
            at <Timestamp seconds={seconds} onSeek={onSeek} />
          </>
        )}
      </span>
    )
  }
  if (consent.inferred_status === "not_found") {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-1.5">
        <SearchX className="size-4" />
        No consent exchange found in the transcript
      </span>
    )
  }
  if (consent.mode === "confirmed") return null
  return (
    <span className="text-muted-foreground inline-flex items-center gap-1.5">
      <Loader2 className="size-4 animate-spin" />
      Automatic detection has not finished
    </span>
  )
}

function AttestedLine({ consent }: { consent: MeetingConsent }) {
  const by = consent.attested_by?.full_name ?? "an advisor"
  const on = consent.attested_at ? format(new Date(consent.attested_at), "MMM d, yyyy 'at' h:mm a") : null
  if (consent.attested_status === "granted") {
    return (
      <span className="text-green-accent inline-flex items-center gap-1.5">
        <ShieldCheck className="size-4" />
        Consent confirmed by {by}
        {on && ` on ${on}`}
      </span>
    )
  }
  return (
    <span className="text-red-accent inline-flex items-center gap-1.5">
      <CircleX className="size-4" />
      No consent recorded by {by}
      {on && ` on ${on}`}. The transcript, recording and AI notes were deleted.
    </span>
  )
}

export default function MeetingConsentCard({ meeting, onSeek, compact = false }: MeetingConsentCardProps) {
  const consent = meeting.meeting_consent
  const attest = useAttestMeetingConsent()
  const [choice, setChoice] = useState<MeetingConsentAttestedStatus | undefined>(
    consent?.inferred_status === "declined"
      ? "declined"
      : consent?.inferred_status === "granted"
        ? "granted"
        : undefined
  )
  const [confirmDecline, setConfirmDecline] = useState(false)

  if (!consent) return null

  const pendingAttestation = consent.attestation_required && !consent.attested_at
  const submit = (status: MeetingConsentAttestedStatus) =>
    attest.mutate({ meetingId: meeting.id, attestedStatus: status })

  if (!pendingAttestation) {
    return (
      <div className={cn("text-muted-foreground flex flex-col gap-1 text-sm", compact ? "px-0 py-2" : "px-4 py-3")}>
        {consent.attested_at ? <AttestedLine consent={consent} /> : null}
        {consent.inferred_status && <InferredLine consent={consent} onSeek={onSeek} />}
        {!consent.attested_at && !consent.inferred_status && <InferredLine consent={consent} onSeek={onSeek} />}
        {consent.inferred_quote && (
          <blockquote className="border-l-2 pl-3 text-xs whitespace-pre-line">{consent.inferred_quote}</blockquote>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "border-yellow-border bg-yellow-background/40 space-y-3 border-b text-sm",
        compact ? "px-0 py-3" : "px-4 py-3"
      )}
    >
      <div className="space-y-1">
        <div className="font-medium">Confirm client consent</div>
        <p className="text-muted-foreground text-xs">
          AI notes for this meeting are shown once you confirm the client agreed to be recorded.
        </p>
        <InferredLine consent={consent} onSeek={onSeek} />
        {consent.inferred_quote && (
          <blockquote className="text-muted-foreground border-l-2 pl-3 text-xs whitespace-pre-line">
            {consent.inferred_quote}
          </blockquote>
        )}
      </div>
      <RadioGroup
        value={choice ?? ""}
        onValueChange={(value) => setChoice(value === "granted" || value === "declined" ? value : undefined)}
        className="gap-1.5"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="granted" id={`${meeting.id}-consent-granted`} />
          <Label htmlFor={`${meeting.id}-consent-granted`} className="cursor-pointer font-normal">
            Client consented to being recorded
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="declined" id={`${meeting.id}-consent-declined`} />
          <Label htmlFor={`${meeting.id}-consent-declined`} className="cursor-pointer font-normal">
            Client did not consent
          </Label>
        </div>
      </RadioGroup>
      <Button
        type="button"
        size="sm"
        variant={choice === "declined" ? "destructive" : "primary"}
        disabled={!choice || attest.isPending}
        onClick={() => (choice === "declined" ? setConfirmDecline(true) : choice && submit(choice))}
      >
        {attest.isPending && <Loader2 className="animate-spin" />}
        {choice === "declined" ? "Record no consent" : "Confirm consent"}
      </Button>
      <ConfirmDialog
        open={confirmDecline}
        onOpenChange={setConfirmDecline}
        title="Delete this meeting's transcript, recording and AI notes?"
        description="Recording that the client did not consent deletes the transcript, the recording and the AI notes for this meeting. Your own notes and attachments are kept. This cannot be undone."
        action={{
          label: "Delete and record no consent",
          destructive: true,
          pending: attest.isPending,
          onClick: () => {
            submit("declined")
            setConfirmDecline(false)
          },
        }}
      />
    </div>
  )
}
